import { getLang, onLangChange } from './translations';
import type { RegionData } from './types';

interface BarInstance {
    container:     HTMLElement;
    trigger:       HTMLButtonElement;
    triggerLabel:  HTMLElement;
    dropdown:      HTMLElement;
}

const bars: BarInstance[] = [];

let regions:    RegionData[] = [];
let activeSlug: string | null = null;
let onChange:   ((region: RegionData) => Promise<void>) | null = null;

function regionName(region: RegionData): string {
    const l  = getLang();
    const cs = region.name_cs || region.name;
    const en = region.name_en || region.name;
    return (l === 'cs' ? cs : en) || cs || '';
}

function openDropdown(bar: BarInstance): void {
    bar.dropdown.classList.remove('hidden');
    bar.trigger.setAttribute('aria-expanded', 'true');
    bar.trigger.classList.add('open');
}

function closeDropdown(bar: BarInstance): void {
    bar.dropdown.classList.add('hidden');
    bar.trigger.setAttribute('aria-expanded', 'false');
    bar.trigger.classList.remove('open');
}

function closeAllDropdowns(): void {
    bars.forEach(closeDropdown);
}

function renderDropdown(bar: BarInstance): void {
    bar.dropdown.innerHTML = '';
    for (const region of regions) {
        const item = document.createElement('button');
        item.className    = 'region-item';
        item.role         = 'option';
        item.textContent  = regionName(region);
        item.dataset.slug = region.slug;
        item.setAttribute('aria-selected', String(region.slug === activeSlug));
        item.classList.toggle('active', region.slug === activeSlug);
        item.addEventListener('click', e => {
            e.stopPropagation();
            closeDropdown(bar);
            activateRegion(region.slug, { triggerChange: true, urlUpdate: 'push' });
        });
        bar.dropdown.appendChild(item);
    }
    updateTriggerLabel(bar);
}

function updateTriggerLabel(bar: BarInstance): void {
    const active = regions.find(r => r.slug === activeSlug);
    bar.triggerLabel.textContent = active ? regionName(active) : '';
}

function renderAllDropdowns(): void {
    bars.forEach(renderDropdown);
}

onLangChange(() => { if (regions.length) renderAllDropdowns(); });

function buildBar(container: HTMLElement): BarInstance {
    const trigger = document.createElement('button');
    trigger.id = `${container.id}-trigger`;
    trigger.className = 'region-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    // Location pin icon
    const pinSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    pinSvg.setAttribute('width', '13');
    pinSvg.setAttribute('height', '16');
    pinSvg.setAttribute('viewBox', '0 0 13 16');
    pinSvg.setAttribute('fill', 'none');
    pinSvg.setAttribute('aria-hidden', 'true');
    pinSvg.classList.add('region-pin');
    const pinCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    pinCircle.setAttribute('cx', '6.5'); pinCircle.setAttribute('cy', '6'); pinCircle.setAttribute('r', '2.2');
    pinCircle.setAttribute('stroke', 'currentColor'); pinCircle.setAttribute('stroke-width', '1.4');
    const pinPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pinPath.setAttribute('d', 'M6.5 1C4 1 2 3 2 5.5 2 8.5 6.5 14 6.5 14S11 8.5 11 5.5C11 3 9 1 6.5 1Z');
    pinPath.setAttribute('stroke', 'currentColor'); pinPath.setAttribute('stroke-width', '1.4');
    pinPath.setAttribute('stroke-linejoin', 'round');
    pinSvg.appendChild(pinCircle);
    pinSvg.appendChild(pinPath);
    trigger.appendChild(pinSvg);

    const triggerLabel = document.createElement('span');
    triggerLabel.className = 'region-trigger-name';
    trigger.appendChild(triggerLabel);

    const triggerChevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    triggerChevron.setAttribute('width',  '10');
    triggerChevron.setAttribute('height', '10');
    triggerChevron.setAttribute('viewBox', '0 0 10 10');
    triggerChevron.setAttribute('fill', 'none');
    triggerChevron.setAttribute('aria-hidden', 'true');
    triggerChevron.classList.add('region-chevron');
    const chevPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    chevPath.setAttribute('d', 'M2 3.5L5 6.5L8 3.5');
    chevPath.setAttribute('stroke', 'currentColor');
    chevPath.setAttribute('stroke-width', '1.5');
    chevPath.setAttribute('stroke-linecap', 'round');
    chevPath.setAttribute('stroke-linejoin', 'round');
    triggerChevron.appendChild(chevPath);
    trigger.appendChild(triggerChevron);

    const dropdown = document.createElement('div');
    dropdown.className = 'region-dropdown';
    dropdown.setAttribute('role', 'listbox');
    dropdown.classList.add('hidden');

    container.appendChild(trigger);
    container.appendChild(dropdown);

    const bar: BarInstance = { container, trigger, triggerLabel, dropdown };

    trigger.addEventListener('click', e => {
        e.stopPropagation();
        if (dropdown.classList.contains('hidden')) {
            closeAllDropdowns();
            openDropdown(bar);
        } else {
            closeDropdown(bar);
        }
    });

    return bar;
}

document.addEventListener('click', () => closeAllDropdowns());

export function initRegionBars(containerIds: string[]): void {
    for (const id of containerIds) {
        const container = document.getElementById(id);
        if (!container) continue;
        bars.push(buildBar(container));
    }
}

function slugFromUrl(): string | null {
    const seg = location.pathname.replace(/^\/+|\/+$/g, '');
    return seg || null;
}

export async function loadRegions(
    onChangeFn: (region: RegionData) => Promise<void>,
): Promise<RegionData | null> {
    onChange = onChangeFn;
    regions  = await fetch('/api/regions').then(r => r.json()) as RegionData[];

    if (regions.length === 0) return null;

    bars.forEach(bar => {
        bar.container.classList.remove('hidden');
        if (regions.length === 1) bar.trigger.disabled = true;
    });

    // Unknown or missing slug in the URL falls back to the first region in
    // sort order (the API-provided default).
    const urlSlug  = slugFromUrl();
    const initial  = (urlSlug && regions.some(r => r.slug === urlSlug)) ? urlSlug : regions[0].slug;

    window.addEventListener('popstate', () => {
        const slug   = slugFromUrl();
        const region = (slug && regions.find(r => r.slug === slug)) || regions[0];
        if (region.slug !== activeSlug) activateRegion(region.slug, { triggerChange: true, urlUpdate: 'none' });
    });

    return activateRegion(initial, { triggerChange: false, urlUpdate: 'replace' });
}

async function activateRegion(
    slug: string,
    opts: { triggerChange: boolean; urlUpdate: 'push' | 'replace' | 'none' },
): Promise<RegionData | null> {
    activeSlug = slug;
    renderAllDropdowns();

    if (opts.urlUpdate !== 'none') {
        const path = '/' + slug;
        if (location.pathname !== path) {
            if (opts.urlUpdate === 'push') history.pushState({ slug }, '', path);
            else history.replaceState({ slug }, '', path);
        }
    }

    const region = regions.find(r => r.slug === slug) ?? null;
    if (opts.triggerChange && onChange && region) await onChange(region);
    return region;
}

export function getActiveSlug(): string | null { return activeSlug; }
